import { useState, useCallback, useRef } from 'react';
import { ImageOff } from 'lucide-react';
import { API_BASE } from '@/lib/api';

// Use same-origin URL on kinsy.io so the auth cookie is sent automatically by
// the browser. Previous implementation used axios blob fetch which hangs on
// mobile Safari when the URL is cross-origin (bundle env URL ≠ kinsy.io).
export default function AlbumCover({ fileId }) {
  const [errored, setErrored] = useState(false);
  // Once we've kicked off the full-res preload we never repeat it — the
  // browser has it in HTTP cache and any second fetch would be a wasted
  // 304 round-trip. `useRef` because we don't want a state change to
  // re-render the tile.
  const preloadedRef = useRef(false);

  /**
   * Warm the browser's HTTP cache with the FULL-RES image the moment
   * the user's intent to open the lightbox becomes visible: hover
   * (desktop) or touchstart (iOS tap-down, ~100ms before the click
   * event fires). By the time the lightbox mounts, the full-res
   * bytes are usually already in cache and paint is instant.
   *
   * Cheap: one `new Image()` per tile, one time, only after intent.
   * Never on scroll — we don't want to preload every tile a user
   * sweeps past. Only tiles the user's cursor / finger lands on.
   */
  const preloadFullRes = useCallback(() => {
    if (!fileId || preloadedRef.current) return;
    preloadedRef.current = true;
    try {
      const img = new Image();
      img.decoding = 'async';
      img.src = `${API_BASE}/files/${fileId}`;
    } catch (_) { /* ignore — best-effort */ }
  }, [fileId]);

  if (!fileId) {
    return (
      <div className="w-full h-full bg-stone-100 flex flex-col items-center justify-center text-stone-400 p-4" data-testid="album-cover-empty">
        <ImageOff className="w-8 h-8 mb-2" />
        <span className="text-xs">No image</span>
      </div>
    );
  }

  if (errored) {
    return (
      <div className="w-full h-full bg-stone-100 flex flex-col items-center justify-center text-stone-400 p-4" data-testid="album-cover-error">
        <ImageOff className="w-8 h-8 mb-2" />
        <span className="text-xs">Failed to load</span>
      </div>
    );
  }

  return (
    <img
      src={`${API_BASE}/files/${fileId}?w=600`}
      alt="Album photo"
      loading="lazy"
      decoding="async"
      onMouseEnter={preloadFullRes}
      onTouchStart={preloadFullRes}
      onFocus={preloadFullRes}
      className="w-full h-full object-cover"
      onError={() => setErrored(true)}
      data-testid="album-cover-image"
    />
  );
}
